/**
 * Section Library — Advanced Section Manager
 *
 * Features:
 * - Tab navigation: Templates / Current Sections / Favorites
 * - 8 generic section templates + 38 new section presets from sectionBlockLibrary
 * - About page pre-designed sections from aboutSectionBlocks
 * - Search with category filtering
 * - Favorites system (persisted to localStorage)
 * - Recently used sections
 * - Section preview mini cards
 * - One-click add with toast feedback
 * - Drag handle for future drag-to-position
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  X, Layout, Image, Type, ShoppingBag, Star,
  MessageCircle, Mail, HelpCircle, Layers, Search, Plus,
  Users, BarChart3, Clock, Heart, Camera, Quote, Megaphone, Compass, Phone,
  Bookmark, BookmarkCheck, Zap, Gift, Sparkles, Grid, ArrowRight,
  Mountain, Trophy, Target, MessageSquare, Newspaper, CheckCircle
} from 'lucide-react';
import aboutSectionBlocks from './aboutSectionBlocks';
import { allSectionBlocks, SECTION_BLOCK_CATEGORIES } from './sectionBlockLibrary';

// ── Category definitions ─────────────────────────────────────────────
const SECTION_CATEGORIES = {
  'Navigation': { icon: Layout, match: ['navbar','header','nav','menu'] },
  'Hero': { icon: Image, match: ['hero','banner','slider','jumbotron'] },
  'Content': { icon: Type, match: ['about','story','content','text','features','intro','brand','announcement','trust','logo','lookbook','process','size','shipping','tabs'] },
  'Products': { icon: ShoppingBag, match: ['product','collection','shop','category','catalog','trending','arrivals','sellers','comparison','recently','collections'] },
  'Testimonials': { icon: Star, match: ['testimonial','review','rating','video_testimonial'] },
  'Contact': { icon: Mail, match: ['contact','newsletter','subscribe','email'] },
  'FAQ': { icon: HelpCircle, match: ['faq','question','help'] },
  'Social': { icon: MessageCircle, match: ['social','instagram','gallery'] },
  'CTA': { icon: Megaphone, match: ['cta','call-to-action','promo','banner-cta','sale','countdown','whatsapp'] },
  'E-commerce': { icon: ShoppingBag, match: ['collection-showcase','collection-banner','categories-grid'] },
  'Stats': { icon: BarChart3, match: ['stats','numbers','counter','metric'] },
  'Team': { icon: Users, match: ['team','member','staff','founder'] },
  'Other': { icon: Layers, match: [] }
};

const ABOUT_CATEGORY_ICONS = {
  'Hero': Image, 'Story': Type, 'Team': Users, 'Stats': BarChart3,
  'Timeline': Clock, 'Values': Heart, 'Gallery': Camera,
  'Content': Quote, 'CTA': Megaphone, 'Process': Compass, 'Contact': Phone,
};

// ── Generic Section Templates (works for any page type) ──────────────
const GENERIC_SECTION_TEMPLATES = [
  {
    id: 'hero-centered',
    category: 'Hero',
    label: 'Centered Hero',
    description: 'Full-width hero with centered text and CTA button',
    icon: Mountain,
    block: {
      id: 'hero-centered',
      type: 'section',
      enabled: true,
      structure: {
        type: 'div',
        className: 'relative min-h-[80vh] flex items-center justify-center bg-gray-900 text-white',
        style: { minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#111827', color: '#ffffff', textAlign: 'center', padding: '80px 24px' },
        children: [
          {
            type: 'div',
            style: { maxWidth: '700px', margin: '0 auto' },
            children: [
              { type: 'h1', content: 'Your Brand Story', style: { fontSize: '56px', fontWeight: '700', lineHeight: '1.1', marginBottom: '20px', letterSpacing: '-0.03em' } },
              { type: 'p', content: 'Crafting timeless fashion that speaks to who you are. Discover our latest collection designed with passion and purpose.', style: { fontSize: '18px', lineHeight: '1.7', opacity: '0.8', marginBottom: '32px' } },
              { type: 'a', content: 'Shop Now', attributes: { href: '/shop' }, style: { display: 'inline-block', padding: '14px 36px', backgroundColor: '#ffffff', color: '#111827', borderRadius: '8px', fontSize: '15px', fontWeight: '600', textDecoration: 'none' } },
            ],
          },
        ],
      },
    },
  },
  {
    id: 'hero-split',
    category: 'Hero',
    label: 'Split Hero',
    description: 'Two-column hero with image and text side by side',
    icon: Layout,
    block: {
      id: 'hero-split',
      type: 'section',
      enabled: true,
      structure: {
        type: 'div',
        style: { display: 'flex', minHeight: '70vh', flexWrap: 'wrap' },
        children: [
          {
            type: 'div',
            style: { flex: '1 1 50%', minWidth: '300px', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '60px 48px' },
            children: [
              { type: 'span', content: 'New Collection', style: { fontSize: '13px', fontWeight: '600', color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '12px', display: 'block' } },
              { type: 'h1', content: 'Elegance Redefined', style: { fontSize: '48px', fontWeight: '700', lineHeight: '1.15', marginBottom: '20px', color: '#111827', letterSpacing: '-0.02em' } },
              { type: 'p', content: 'Explore our curated collection of handcrafted pieces that blend traditional artistry with modern design.', style: { fontSize: '16px', lineHeight: '1.7', color: '#64748b', marginBottom: '32px' } },
              { type: 'a', content: 'Explore Collection', attributes: { href: '/shop' }, style: { display: 'inline-block', padding: '12px 32px', backgroundColor: '#111827', color: '#ffffff', borderRadius: '8px', fontSize: '14px', fontWeight: '600', textDecoration: 'none' } },
            ],
          },
          {
            type: 'div',
            style: { flex: '1 1 50%', minWidth: '300px', minHeight: '400px', backgroundColor: '#f3f4f6', backgroundSize: 'cover', backgroundPosition: 'center' },
            children: [
              { type: 'img', attributes: { src: '/static/images/placeholder.jpg', alt: 'Collection' }, style: { width: '100%', height: '100%', objectFit: 'cover' } },
            ],
          },
        ],
      },
    },
  },
  {
    id: 'features-grid',
    category: 'Content',
    label: 'Feature Grid',
    description: '3-column grid showcasing key features with icons',
    icon: Grid,
    block: {
      id: 'features-grid',
      type: 'section',
      enabled: true,
      structure: {
        type: 'div',
        style: { padding: '80px 24px', maxWidth: '1200px', margin: '0 auto' },
        children: [
          { type: 'h2', content: 'Why Choose Us', style: { fontSize: '36px', fontWeight: '700', textAlign: 'center', marginBottom: '48px', color: '#111827', letterSpacing: '-0.02em' } },
          {
            type: 'div',
            style: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '32px' },
            children: [
              { type: 'div', style: { textAlign: 'center', padding: '24px' }, children: [
                { type: 'div', style: { width: '56px', height: '56px', borderRadius: '12px', backgroundColor: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: '24px' }, content: '\u2728' },
                { type: 'h3', content: 'Handcrafted Quality', style: { fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: '#111827' } },
                { type: 'p', content: 'Every piece is carefully crafted by skilled artisans using premium materials.', style: { fontSize: '14px', lineHeight: '1.6', color: '#64748b' } },
              ]},
              { type: 'div', style: { textAlign: 'center', padding: '24px' }, children: [
                { type: 'div', style: { width: '56px', height: '56px', borderRadius: '12px', backgroundColor: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: '24px' }, content: '\uD83D\uDE9A' },
                { type: 'h3', content: 'Free Shipping', style: { fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: '#111827' } },
                { type: 'p', content: 'Enjoy free delivery on all orders. We ship across India with care.', style: { fontSize: '14px', lineHeight: '1.6', color: '#64748b' } },
              ]},
              { type: 'div', style: { textAlign: 'center', padding: '24px' }, children: [
                { type: 'div', style: { width: '56px', height: '56px', borderRadius: '12px', backgroundColor: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: '24px' }, content: '\uD83D\uDC8E' },
                { type: 'h3', content: 'Premium Fabrics', style: { fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: '#111827' } },
                { type: 'p', content: 'Sourced from the finest mills, our fabrics are soft, durable, and beautiful.', style: { fontSize: '14px', lineHeight: '1.6', color: '#64748b' } },
              ]},
            ],
          },
        ],
      },
    },
  },
  {
    id: 'cta-banner',
    category: 'CTA',
    label: 'CTA Banner',
    description: 'Bold call-to-action banner with gradient background',
    icon: Megaphone,
    block: {
      id: 'cta-banner',
      type: 'section',
      enabled: true,
      structure: {
        type: 'div',
        style: { padding: '80px 24px', background: 'linear-gradient(135deg, #1e1b4b, #312e81)', textAlign: 'center', color: '#ffffff' },
        children: [
          { type: 'h2', content: 'Ready to Elevate Your Style?', style: { fontSize: '40px', fontWeight: '700', marginBottom: '16px', letterSpacing: '-0.02em' } },
          { type: 'p', content: 'Join thousands of fashion-forward customers. Get 15% off your first order.', style: { fontSize: '18px', opacity: '0.9', marginBottom: '32px', maxWidth: '500px', margin: '0 auto 32px' } },
          { type: 'a', content: 'Start Shopping', attributes: { href: '/shop' }, style: { display: 'inline-block', padding: '14px 40px', backgroundColor: '#ffffff', color: '#1e1b4b', borderRadius: '10px', fontSize: '15px', fontWeight: '600', textDecoration: 'none' } },
        ],
      },
    },
  },
  {
    id: 'testimonials-carousel',
    category: 'Testimonials',
    label: 'Testimonial Cards',
    description: 'Customer testimonials in a card grid layout',
    icon: Star,
    block: {
      id: 'testimonials-carousel',
      type: 'section',
      enabled: true,
      structure: {
        type: 'div',
        style: { padding: '80px 24px', backgroundColor: '#f9fafb' },
        children: [
          { type: 'h2', content: 'What Our Customers Say', style: { fontSize: '36px', fontWeight: '700', textAlign: 'center', marginBottom: '48px', color: '#111827' } },
          {
            type: 'div',
            style: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px', maxWidth: '1100px', margin: '0 auto' },
            children: [1, 2, 3].map((i) => ({
              type: 'div',
              style: { backgroundColor: '#ffffff', padding: '28px', borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' },
              children: [
                { type: 'div', content: '\u2605\u2605\u2605\u2605\u2605', style: { fontSize: '16px', color: '#f59e0b', marginBottom: '12px', letterSpacing: '2px' } },
                { type: 'p', content: `"Absolutely stunning quality! The fabric is luxurious and the fit is perfect. Will definitely order again."`, style: { fontSize: '14px', lineHeight: '1.6', color: '#374151', marginBottom: '16px' } },
                { type: 'p', content: `Customer ${i}`, style: { fontSize: '13px', fontWeight: '600', color: '#111827' } },
              ],
            })),
          },
        ],
      },
    },
  },
  {
    id: 'newsletter-signup',
    category: 'Contact',
    label: 'Newsletter Signup',
    description: 'Email subscription section with input field',
    icon: Mail,
    block: {
      id: 'newsletter-signup',
      type: 'section',
      enabled: true,
      structure: {
        type: 'div',
        style: { padding: '60px 24px', textAlign: 'center', backgroundColor: '#111827', color: '#ffffff' },
        children: [
          { type: 'h2', content: 'Stay in the Loop', style: { fontSize: '32px', fontWeight: '700', marginBottom: '12px' } },
          { type: 'p', content: 'Subscribe to get exclusive offers, new arrivals, and styling tips.', style: { fontSize: '16px', opacity: '0.8', marginBottom: '28px' } },
          {
            type: 'div',
            style: { display: 'flex', gap: '12px', justifyContent: 'center', maxWidth: '460px', margin: '0 auto' },
            children: [
              { type: 'div', style: { flex: '1', height: '48px', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', padding: '0 16px', color: 'rgba(255,255,255,0.5)', fontSize: '14px' }, content: 'Enter your email' },
              { type: 'a', content: 'Subscribe', attributes: { href: '#' }, style: { display: 'flex', alignItems: 'center', padding: '0 24px', backgroundColor: '#6366f1', color: '#ffffff', borderRadius: '10px', fontSize: '14px', fontWeight: '600', textDecoration: 'none', whiteSpace: 'nowrap' } },
            ],
          },
        ],
      },
    },
  },
  {
    id: 'stats-counter',
    category: 'Stats',
    label: 'Stats Counter',
    description: 'Impressive numbers showcasing brand achievements',
    icon: BarChart3,
    block: {
      id: 'stats-counter',
      type: 'section',
      enabled: true,
      structure: {
        type: 'div',
        style: { padding: '60px 24px', backgroundColor: '#ffffff' },
        children: [
          {
            type: 'div',
            style: { display: 'flex', justifyContent: 'center', gap: '64px', flexWrap: 'wrap', maxWidth: '900px', margin: '0 auto' },
            children: [
              { type: 'div', style: { textAlign: 'center' }, children: [
                { type: 'div', content: '10K+', style: { fontSize: '42px', fontWeight: '800', color: '#111827', letterSpacing: '-0.02em' } },
                { type: 'p', content: 'Happy Customers', style: { fontSize: '14px', color: '#6b7280', marginTop: '4px' } },
              ]},
              { type: 'div', style: { textAlign: 'center' }, children: [
                { type: 'div', content: '500+', style: { fontSize: '42px', fontWeight: '800', color: '#111827', letterSpacing: '-0.02em' } },
                { type: 'p', content: 'Products', style: { fontSize: '14px', color: '#6b7280', marginTop: '4px' } },
              ]},
              { type: 'div', style: { textAlign: 'center' }, children: [
                { type: 'div', content: '50+', style: { fontSize: '42px', fontWeight: '800', color: '#111827', letterSpacing: '-0.02em' } },
                { type: 'p', content: 'Collections', style: { fontSize: '14px', color: '#6b7280', marginTop: '4px' } },
              ]},
              { type: 'div', style: { textAlign: 'center' }, children: [
                { type: 'div', content: '4.9', style: { fontSize: '42px', fontWeight: '800', color: '#111827', letterSpacing: '-0.02em' } },
                { type: 'p', content: 'Rating', style: { fontSize: '14px', color: '#6b7280', marginTop: '4px' } },
              ]},
            ],
          },
        ],
      },
    },
  },
  {
    id: 'spacer-divider',
    category: 'Other',
    label: 'Spacer / Divider',
    description: 'Simple spacing section with optional decorative line',
    icon: Layers,
    block: {
      id: 'spacer-divider',
      type: 'section',
      enabled: true,
      structure: {
        type: 'div',
        style: { padding: '40px 24px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
        children: [
          { type: 'div', style: { width: '80px', height: '2px', backgroundColor: '#e5e7eb', borderRadius: '1px' }, children: [] },
        ],
      },
    },
  },
];

// ── Icon map for sectionBlockLibrary icon strings ─────────────────────
const ICON_STRING_MAP = {
  'Grid3x3': Grid,
  'ArrowLeftRight': ArrowRight,
  'Star': Star,
  'LayoutGrid': Grid,
  'Sparkles': Sparkles,
  'Trophy': Trophy,
  'Columns': Layout,
  'Play': Zap,
  'Layers': Layers,
  'Clock': Clock,
  'Type': Type,
  'Image': Image,
  'BookOpen': Newspaper,
  'Megaphone': Megaphone,
  'ShieldCheck': CheckCircle,
  'Award': Trophy,
  'Instagram': Camera,
  'Camera': Camera,
  'Compass': Compass,
  'Users': Users,
  'HelpCircle': HelpCircle,
  'Phone': Phone,
  'Gift': Gift,
  'SquareStack': Layers,
  'Ruler': Target,
  'Video': Zap,
  'Share2': MessageCircle,
  'BarChart3': BarChart3,
  'Grid': Grid,
  'Percent': Megaphone,
  'Truck': ArrowRight,
  'MessageCircle': MessageCircle,
};

function resolveIcon(icon) {
  if (typeof icon === 'function') return icon;
  if (typeof icon === 'string' && ICON_STRING_MAP[icon]) return ICON_STRING_MAP[icon];
  return Layers;
}

// ── Merge library blocks into template format ────────────────────────
const LIBRARY_SECTION_TEMPLATES = allSectionBlocks.map(section => ({
  ...section,
  id: section.block?.id || section.label,
  icon: resolveIcon(section.icon),
}));

// ── Combined templates: original 8 + 35 new ones ────────────────────
const ALL_TEMPLATES = [
  ...GENERIC_SECTION_TEMPLATES,
  ...LIBRARY_SECTION_TEMPLATES,
];

// ── Helpers ───────────────────────────────────────────────────────────
function getCategoryForBlock(block) {
  const id = (block.id || '').toLowerCase();
  for (const [cat, config] of Object.entries(SECTION_CATEGORIES)) {
    if (cat === 'Other') continue;
    if (config.match.some(m => id.includes(m))) return cat;
  }
  return 'Other';
}

function formatBlockName(block) {
  const id = block.id || block.type || 'section';
  return id.charAt(0).toUpperCase() + id.slice(1).replace(/[-_]/g, ' ');
}

function deepCloneBlock(block) {
  return JSON.parse(JSON.stringify(block));
}

const FAVORITES_KEY = 'vasstr_section_favorites';
const RECENT_KEY = 'vasstr_section_recent';

function loadFavorites() {
  try { return JSON.parse(localStorage.getItem(FAVORITES_KEY) || '[]'); } catch { return []; }
}
function saveFavorites(favs) {
  try { localStorage.setItem(FAVORITES_KEY, JSON.stringify(favs)); } catch {}
}
function loadRecent() {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch { return []; }
}
function addToRecent(id) {
  try {
    let recent = loadRecent().filter(r => r !== id);
    recent.unshift(id);
    if (recent.length > 10) recent = recent.slice(0, 10);
    localStorage.setItem(RECENT_KEY, JSON.stringify(recent));
  } catch {}
}

// ── Category ordering helper ─────────────────────────────────────────
const CATEGORY_ORDER = [
  'Products', 'Hero', 'Content', 'E-commerce', 'CTA', 'Testimonials',
  'Contact', 'FAQ', 'Social', 'Stats', 'Team', 'Other'
];

function sortCategories(categorizedObj) {
  const ordered = {};
  for (const cat of CATEGORY_ORDER) {
    if (categorizedObj[cat]) {
      ordered[cat] = categorizedObj[cat];
    }
  }
  // Add any remaining categories not in the order
  for (const [cat, items] of Object.entries(categorizedObj)) {
    if (!ordered[cat]) {
      ordered[cat] = items;
    }
  }
  return ordered;
}

// ── Main Component ───────────────────────────────────────────────────
export default function SectionLibrary({ templateJSON, onAddSection, onClose, pageType }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('templates'); // 'templates' | 'current' | 'favorites'
  const [favorites, setFavorites] = useState(loadFavorites);
  const [addedId, setAddedId] = useState(null);
  const isAboutPage = pageType === 'about';

  const toggleFavorite = useCallback((id) => {
    setFavorites(prev => {
      const next = prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id];
      saveFavorites(next);
      return next;
    });
  }, []);

  // ── About page sections ──
  const aboutCategorized = useMemo(() => {
    if (!isAboutPage) return {};
    const groups = {};
    for (const section of aboutSectionBlocks) {
      const cat = section.category || 'Other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(section);
    }
    return groups;
  }, [isAboutPage]);

  // ── Template sections (generic + library blocks) ──
  const templateCategorized = useMemo(() => {
    const groups = {};
    const sections = isAboutPage ? [] : ALL_TEMPLATES;
    for (const section of sections) {
      const cat = section.category || 'Other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(section);
    }
    return sortCategories(groups);
  }, [isAboutPage]);

  // ── Current page sections ──
  const currentCategorized = useMemo(() => {
    if (!templateJSON?.blocks) return {};
    const seen = new Set();
    const groups = {};
    for (const block of templateJSON.blocks) {
      const key = block.id || block.type || ('block-' + Math.random());
      if (seen.has(key)) continue;
      seen.add(key);
      const category = getCategoryForBlock(block);
      if (!groups[category]) groups[category] = [];
      groups[category].push(block);
    }
    return groups;
  }, [templateJSON]);

  // ── Favorite sections ──
  const favoriteSections = useMemo(() => {
    const all = [...ALL_TEMPLATES];
    if (isAboutPage) {
      aboutSectionBlocks.forEach(s => all.push({ ...s, id: s.block?.id || s.label }));
    }
    return all.filter(s => favorites.includes(s.id || s.block?.id));
  }, [favorites, isAboutPage]);

  // Determine which sections to show based on active tab
  const activeSections = useMemo(() => {
    if (activeTab === 'favorites') {
      return { 'Favorites': favoriteSections };
    }
    if (activeTab === 'current') return currentCategorized;
    if (isAboutPage) return aboutCategorized;
    return templateCategorized;
  }, [activeTab, isAboutPage, aboutCategorized, templateCategorized, currentCategorized, favoriteSections]);

  // ── Filter by search ──
  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return activeSections;
    const q = searchQuery.toLowerCase();
    const filtered = {};
    for (const [category, items] of Object.entries(activeSections)) {
      const matching = items.filter(item => {
        const label = (item.label || formatBlockName(item)).toLowerCase();
        const desc = (item.description || '').toLowerCase();
        const id = (item.id || '').toLowerCase();
        return label.includes(q) || desc.includes(q) || id.includes(q) || category.toLowerCase().includes(q);
      });
      if (matching.length > 0) filtered[category] = matching;
    }
    return filtered;
  }, [activeSections, searchQuery]);

  const handleAdd = useCallback((item) => {
    let blockToAdd;
    if (item.block) {
      // Template or about section
      blockToAdd = deepCloneBlock(item.block);
      blockToAdd.id = (item.block.id || 'section') + '-' + Date.now();
    } else {
      // Current section (duplicate)
      blockToAdd = deepCloneBlock(item);
      blockToAdd.id = (item.id || 'section') + '-new-' + Date.now();
    }
    blockToAdd.enabled = true;
    onAddSection(blockToAdd);
    addToRecent(item.id || item.block?.id || 'unknown');

    // Brief visual feedback
    setAddedId(item.id || item.block?.id || Math.random());
    setTimeout(() => setAddedId(null), 1000);
  }, [onAddSection]);

  const totalSections = Object.values(filteredCategories).reduce((sum, arr) => sum + arr.length, 0);

  const TABS = [
    { key: 'templates', label: isAboutPage ? 'About Sections' : 'Ready-Made', icon: Sparkles },
    { key: 'current', label: 'On This Page', icon: Layers },
    { key: 'favorites', label: 'Saved', icon: Bookmark, count: favorites.length },
  ];

  return (
    <div style={{
      height: '100%', display: 'flex', flexDirection: 'column',
      background: '#ffffff', borderLeft: '1px solid #e5e7eb',
      width: '320px', minWidth: '320px',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px', background: '#1e293b', color: '#ffffff',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Layers style={{ width: '16px', height: '16px' }} />
          <span style={{ fontSize: '13px', fontWeight: 600 }}>Section Library</span>
          {!isAboutPage && (
            <span style={{
              fontSize: '9px', fontWeight: 700, background: '#6366f1',
              color: '#ffffff', padding: '2px 6px', borderRadius: 4, marginLeft: 4,
            }}>
              {ALL_TEMPLATES.length}
            </span>
          )}
        </div>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer',
          padding: '4px', borderRadius: '4px', display: 'flex', alignItems: 'center',
        }}
          onMouseEnter={(e) => { e.currentTarget.style.color = '#ffffff'; e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = '#94a3b8'; e.currentTarget.style.background = 'none'; }}
        >
          <X style={{ width: '16px', height: '16px' }} />
        </button>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex', borderBottom: '1px solid #e5e7eb', padding: '0 8px', flexShrink: 0,
      }}>
        {TABS.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                padding: '9px 8px', border: 'none', borderBottom: isActive ? '2px solid #6366f1' : '2px solid transparent',
                background: 'transparent', cursor: 'pointer',
                fontSize: 11, fontWeight: isActive ? 600 : 500,
                color: isActive ? '#6366f1' : '#6b7280',
                transition: 'all 0.15s',
              }}
            >
              <Icon style={{ width: 12, height: 12 }} />
              {tab.label}
              {tab.count > 0 && (
                <span style={{
                  fontSize: 9, fontWeight: 700, background: isActive ? '#eef2ff' : '#f1f5f9',
                  color: isActive ? '#6366f1' : '#94a3b8', padding: '1px 5px', borderRadius: 4,
                }}>
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div style={{ padding: '10px 16px', borderBottom: '1px solid #f3f4f6', flexShrink: 0 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '6px 10px',
        }}>
          <Search style={{ width: '14px', height: '14px', color: '#9ca3af', flexShrink: 0 }} />
          <input
            type="text"
            placeholder="Search sections..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: '12px', color: '#374151', width: '100%' }}
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', padding: 0, display: 'flex' }}>
              <X style={{ width: 12, height: 12 }} />
            </button>
          )}
        </div>
        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '6px' }}>
          {totalSections} section{totalSections !== 1 ? 's' : ''} available
        </div>
      </div>

      {/* Section List */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 12px' }}>
        {Object.keys(filteredCategories).length === 0 && (
          <div style={{ textAlign: 'center', padding: '32px 16px', color: '#9ca3af', fontSize: '12px' }}>
            {activeTab === 'favorites' && favorites.length === 0
              ? 'No saved sections yet. Click the bookmark icon on any section to save it for quick access later.'
              : activeTab === 'current' && (!templateJSON?.blocks || templateJSON.blocks.length === 0)
                ? 'This page has no sections yet. Go to the "Ready-Made" tab to add some.'
                : searchQuery
                  ? 'No sections match your search. Try a different keyword.'
                  : 'No sections available.'}
          </div>
        )}

        {Object.entries(filteredCategories).map(([category, items]) => {
          const CategoryIcon = isAboutPage && activeTab === 'templates'
            ? (ABOUT_CATEGORY_ICONS[category] || Layers)
            : (SECTION_CATEGORIES[category]?.icon || Layers);

          return (
            <div key={category} style={{ marginBottom: '16px' }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 4px', marginBottom: '6px',
              }}>
                <CategoryIcon style={{ width: '12px', height: '12px', color: '#6b7280' }} />
                <span style={{ fontSize: '11px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {category}
                </span>
                <span style={{ fontSize: '10px', color: '#9ca3af', marginLeft: 'auto' }}>{items.length}</span>
              </div>

              {items.map((item) => {
                const itemId = item.id || item.block?.id || Math.random();
                const isFav = favorites.includes(itemId);
                const justAdded = addedId === itemId;

                return (
                  <SectionCard
                    key={itemId}
                    label={item.label || formatBlockName(item)}
                    description={item.description || (item.enabled === false ? 'Hidden' : 'Visible')}
                    icon={item.icon || CategoryIcon}
                    category={item.category || category}
                    isFavorite={isFav}
                    onToggleFavorite={() => toggleFavorite(itemId)}
                    onAdd={() => handleAdd(item)}
                    justAdded={justAdded}
                  />
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Section Preview Thumbnail ─────────────────────────────────────────
function SectionPreview({ category }) {
  const previewStyles = {
    Hero: (
      <div style={{ display: 'flex', height: '100%', gap: '4px', padding: '6px' }}>
        <div style={{ flex: 1, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', borderRadius: '4px' }} />
        <div style={{ width: '40%', display: 'flex', flexDirection: 'column', gap: '3px' }}>
          <div style={{ flex: 1, backgroundColor: '#e2e8f0', borderRadius: '3px' }} />
          <div style={{ flex: 1, backgroundColor: '#e2e8f0', borderRadius: '3px' }} />
        </div>
      </div>
    ),
    Products: (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '3px', padding: '6px', height: '100%' }}>
        {[1,2,3,4].map(i => (
          <div key={i} style={{ backgroundColor: '#e2e8f0', borderRadius: '3px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 2, backgroundColor: '#cbd5e1', borderRadius: '3px 3px 0 0' }} />
            <div style={{ flex: 1, padding: '2px' }}>
              <div style={{ height: '2px', width: '70%', backgroundColor: '#94a3b8', borderRadius: '1px', marginBottom: '2px' }} />
              <div style={{ height: '2px', width: '40%', backgroundColor: '#94a3b8', borderRadius: '1px' }} />
            </div>
          </div>
        ))}
      </div>
    ),
    Content: (
      <div style={{ padding: '8px 12px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '4px' }}>
        <div style={{ height: '4px', width: '50%', backgroundColor: '#6366f1', borderRadius: '2px' }} />
        <div style={{ height: '3px', width: '90%', backgroundColor: '#cbd5e1', borderRadius: '2px' }} />
        <div style={{ height: '3px', width: '80%', backgroundColor: '#cbd5e1', borderRadius: '2px' }} />
        <div style={{ height: '3px', width: '60%', backgroundColor: '#e2e8f0', borderRadius: '2px' }} />
      </div>
    ),
    Testimonials: (
      <div style={{ display: 'flex', gap: '4px', padding: '6px', height: '100%', alignItems: 'stretch' }}>
        {[1,2,3].map(i => (
          <div key={i} style={{ flex: 1, backgroundColor: '#ffffff', borderRadius: '4px', border: '1px solid #e2e8f0', padding: '4px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
            <div style={{ display: 'flex', gap: '2px' }}>
              {[1,2,3,4,5].map(s => (
                <div key={s} style={{ width: '4px', height: '4px', backgroundColor: '#f59e0b', borderRadius: '1px' }} />
              ))}
            </div>
            <div style={{ height: '2px', width: '90%', backgroundColor: '#e2e8f0', borderRadius: '1px' }} />
            <div style={{ height: '2px', width: '60%', backgroundColor: '#e2e8f0', borderRadius: '1px' }} />
          </div>
        ))}
      </div>
    ),
    Contact: (
      <div style={{ padding: '8px 12px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: '4px' }}>
        <div style={{ height: '4px', width: '40%', backgroundColor: '#6366f1', borderRadius: '2px' }} />
        <div style={{ display: 'flex', gap: '4px', width: '80%' }}>
          <div style={{ flex: 1, height: '10px', backgroundColor: '#e2e8f0', borderRadius: '3px' }} />
          <div style={{ width: '30%', height: '10px', backgroundColor: '#6366f1', borderRadius: '3px' }} />
        </div>
      </div>
    ),
    FAQ: (
      <div style={{ padding: '6px 10px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '3px' }}>
        {[1,2,3].map(i => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <div style={{ width: '6px', height: '6px', borderRadius: '2px', backgroundColor: '#6366f1', flexShrink: 0 }} />
            <div style={{ height: '3px', flex: 1, backgroundColor: '#e2e8f0', borderRadius: '2px' }} />
          </div>
        ))}
      </div>
    ),
    Social: (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '3px', padding: '6px', height: '100%' }}>
        {[1,2,3,4,5,6].map(i => (
          <div key={i} style={{ backgroundColor: i % 2 === 0 ? '#e2e8f0' : '#cbd5e1', borderRadius: '3px' }} />
        ))}
      </div>
    ),
    CTA: (
      <div style={{ height: '100%', background: 'linear-gradient(135deg, #1e1b4b, #312e81)', borderRadius: '0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px', padding: '6px' }}>
        <div style={{ height: '4px', width: '60%', backgroundColor: 'rgba(255,255,255,0.6)', borderRadius: '2px' }} />
        <div style={{ height: '3px', width: '40%', backgroundColor: 'rgba(255,255,255,0.3)', borderRadius: '2px' }} />
        <div style={{ height: '8px', width: '30%', backgroundColor: '#ffffff', borderRadius: '3px', marginTop: '2px' }} />
      </div>
    ),
    'E-commerce': (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '3px', padding: '6px', height: '100%' }}>
        {[1,2,3].map(i => (
          <div key={i} style={{ backgroundColor: '#e2e8f0', borderRadius: '4px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 3, backgroundColor: '#cbd5e1', borderRadius: '4px 4px 0 0' }} />
            <div style={{ flex: 1, padding: '3px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ height: '3px', width: '60%', backgroundColor: '#94a3b8', borderRadius: '1px' }} />
            </div>
          </div>
        ))}
      </div>
    ),
    Stats: (
      <div style={{ display: 'flex', gap: '6px', padding: '8px 12px', height: '100%', alignItems: 'flex-end' }}>
        {[60, 80, 45, 90].map((h, i) => (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
            <div style={{ width: '100%', height: `${h}%`, backgroundColor: i === 3 ? '#6366f1' : '#e2e8f0', borderRadius: '3px 3px 0 0' }} />
            <div style={{ height: '2px', width: '80%', backgroundColor: '#cbd5e1', borderRadius: '1px', marginTop: '3px' }} />
          </div>
        ))}
      </div>
    ),
    Team: (
      <div style={{ display: 'flex', gap: '6px', padding: '6px 10px', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
        {[1,2,3].map(i => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
            <div style={{ width: '16px', height: '16px', borderRadius: '50%', backgroundColor: '#cbd5e1' }} />
            <div style={{ height: '2px', width: '20px', backgroundColor: '#e2e8f0', borderRadius: '1px' }} />
          </div>
        ))}
      </div>
    ),
    Navigation: (
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', height: '100%' }}>
        <div style={{ width: '16px', height: '10px', backgroundColor: '#6366f1', borderRadius: '2px' }} />
        <div style={{ flex: 1 }} />
        {[1,2,3,4].map(i => (
          <div key={i} style={{ height: '3px', width: '18px', backgroundColor: '#cbd5e1', borderRadius: '1px' }} />
        ))}
      </div>
    ),
  };

  return (
    <div style={{
      height: '56px', backgroundColor: '#f8fafc', borderRadius: '8px 8px 0 0',
      border: '1px solid #e2e8f0', borderBottom: 'none', overflow: 'hidden',
    }}>
      {previewStyles[category] || previewStyles.Content}
    </div>
  );
}

// ── Section Card ─────────────────────────────────────────────────────
function SectionCard({ label, description, icon: Icon, onAdd, isFavorite, onToggleFavorite, justAdded, category }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      style={{
        marginBottom: '6px', borderRadius: '8px',
        border: '1px solid', borderColor: justAdded ? '#86efac' : (hovered ? '#d1d5db' : '#e2e8f0'),
        cursor: 'pointer', transition: 'all 0.15s ease',
        overflow: 'hidden',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Thumbnail Preview */}
      <SectionPreview category={category} />

      {/* Card Body */}
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          padding: '8px 10px',
          background: justAdded ? '#ecfdf5' : (hovered ? '#f3f4f6' : '#fafafa'),
          transition: 'background 0.15s ease',
        }}
      >
        {/* Icon */}
        <div
          style={{
            width: '32px', height: '32px', borderRadius: '6px',
            background: justAdded ? '#dcfce7' : (hovered ? '#e0e7ff' : '#e5e7eb'),
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            transition: 'background 0.15s ease',
          }}
          onClick={onAdd}
        >
          {justAdded ? (
            <CheckCircle style={{ width: '14px', height: '14px', color: '#16a34a' }} />
          ) : (
            <Icon style={{ width: '14px', height: '14px', color: hovered ? '#4f46e5' : '#6b7280', transition: 'color 0.15s ease' }} />
          )}
        </div>

        {/* Label */}
        <div style={{ flex: 1, minWidth: 0 }} onClick={onAdd}>
          <div style={{ fontSize: '12px', fontWeight: 500, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {label}
          </div>
          <div style={{ fontSize: '10px', color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {description}
          </div>
        </div>

        {/* Favorite toggle */}
        <button
          onClick={(e) => { e.stopPropagation(); onToggleFavorite(); }}
          style={{
            opacity: hovered || isFavorite ? 1 : 0, transition: 'opacity 0.15s ease',
            background: 'none', border: 'none', cursor: 'pointer', padding: 2,
            color: isFavorite ? '#f59e0b' : '#cbd5e1', display: 'flex',
          }}
          title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
        >
          {isFavorite ? <BookmarkCheck style={{ width: '14px', height: '14px' }} /> : <Bookmark style={{ width: '14px', height: '14px' }} />}
        </button>

        {/* Add button */}
        <div
          onClick={onAdd}
          style={{
            opacity: hovered ? 1 : 0, transition: 'opacity 0.15s ease',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: '24px', height: '24px', borderRadius: '6px',
            background: '#6366f1', flexShrink: 0, cursor: 'pointer',
          }}
        >
          <Plus style={{ width: '12px', height: '12px', color: '#ffffff' }} />
        </div>
      </div>
    </div>
  );
}
